/**
 * Der Spalteneditor der Bestellliste „Bestellliste anpassen" - `renderOrderListEditor`
 * (index.html:7427) samt Huelle (`index.html:3839-3858`), `openOrderListEditor` (7410),
 * `setOrderListColumnField` (7444), `moveOrderListColumn` (7450), `addOrderListColumn` (7460),
 * `deleteOrderListColumn` (7472) und `saveOrderListEditor` (7483).
 *
 * Baseline: `board-listeneditor`. Wie der Board-Editor NICHT vom Bildvergleich ausgenommen.
 *
 * ================= Der Unterschied zum Board-Editor, in einem Satz =================
 *
 * Der Board-Editor bearbeitet WAS es gibt (Statusspalten mit Farbe und Symbol); dieser hier
 * bearbeitet, WAS MAN SIEHT (Sichtbarkeit, Beschriftung, Reihenfolge) - und er kann eigene
 * Textfelder anlegen und wieder wegnehmen.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Der Entwurf ist eine Kopie, nach `position` sortiert (7412-7413), Rueckfall
 *    `DEFAULT_ORDER_LIST_COLUMNS`. Der Editor ruft `normalizeOrderListColumns` NICHT
 *    (Bericht: B5) - anders als der LADEPFAD, der es seit dem 2026-08-25 tut
 *    (`state/Speicher.tsx`, Zweig 1 von `bestandLaden`). Weil der Ladepfad filtert, kann ein
 *    Datensatz mit einem der zehn entfernten Schluessel (`REMOVED_ORDER_LIST_KEYS`) den Editor
 *    gar nicht mehr erreichen - die fehlende Filterung hier bleibt damit folgenlos.
 * 2. `setOrderListColumnField` laesst NUR `label` und `visible` durch (7446) - `visible` als
 *    Wahrheitswert von der Checkbox, nicht als Text.
 * 3. Sichtbarkeit wird durchgehend als `column.visible !== false` gelesen (7433, 7489, 7490, 7495):
 *    ein FEHLENDES Feld gilt als sichtbar. Nur ein ausdrueckliches `false` blendet aus.
 * 4. `maxlength` ist hier **50**, im Board-Editor 40 (7434 gegen 6493). Kein Tippfehler, sondern
 *    zwei verschiedene Werte im Bestand.
 * 5. Die Art der Spalte ist ein reiner Anzeigetext: „Eigenes Textfeld" bei `custom`, sonst
 *    „Standardfeld" (7435).
 * 6. Der Loeschknopf ist bei Standardspalten GESPERRT (`column.custom ? '' : 'disabled'`, 7439) -
 *    andere Bedingung als im Board-Editor, wo die Sperre an der Restzahl haengt. `deleteOrderListColumn`
 *    prueft `!column?.custom` zusaetzlich (7475).
 * 7. Vier Sperren beim Uebernehmen (7485-7489): leerer Entwurf (still), leerer Name, doppelter Name
 *    (ohne Ruecksicht auf Gross-/Kleinschreibung) und - anders als beim Board-Editor - „Mindestens
 *    eine Spalte muss sichtbar bleiben".
 * 8. `addOrderListColumn` (7460): Obergrenze **40**; `id` UND `key` sind derselbe Wert
 *    `'custom_' + uid()`. Das Praefix ist tragend - `setOrderListCustomValue` (7585) nimmt nur
 *    Schluessel an, die mit `custom_` beginnen.
 * 9. `deleteOrderListColumn` (7472) zaehlt die Bestellungen mit einem NICHTLEEREN Wert in
 *    `customFields[key]` (getrimmt) und fragt nur dann zurueck. Der Schluessel wird in
 *    `orderListRemovedCustom` gemerkt; erst das Uebernehmen loescht die Werte aus den Bestellungen
 *    (7491-7494).
 * 10. Beim Uebernehmen wird `visible` NORMALISIERT (`column.visible !== false`, 7490) - danach steht
 *    ueberall ein echter Wahrheitswert, auch wenn das Feld vorher fehlte.
 * 11. Zwei Aufraeumschritte nach dem Speichern, die AUSSERHALB dieses Dialogs wirken (7495-7497):
 *    Spaltenfilter unsichtbarer Spalten werden verworfen, und zeigt die Sortierung auf eine
 *    unsichtbare Spalte, springt sie auf die erste sichtbare mit `direction: 'asc'`. Beide sind hier
 *    als reine Funktionen ausgelagert (`bereinigteFilter`, `bereinigteSortierung`), weil der Zustand
 *    dafuer in `BoardSeite` liegt - der Aufrufer wendet sie an.
 *
 * ================= Was hier NICHT ist =================
 *
 * `save()`, `renderActiveOrderTable()` und `toast(...)` sind Wirkungen ausserhalb des Dialogs und
 * stehen als Naht heraus. `hasPermission('manage_settings')` (7411, 7484) prueft im Bestand der
 * oeffnende und der speichernde Pfad - dieselbe Regelung wie im Board-Editor.
 */
import { useRef, useState } from "react";
import { DEFAULT_ORDER_LIST_COLUMNS, type OrderListColumn } from "../../config/board.ts";
import { EIGENE_SPALTE_PRAEFIX } from "../../lib/orderListEditor.ts";
import type { Order } from "../../lib/types.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import { Ikone } from "../Ikone.tsx";

/** index.html:7462 - mehr Listenspalten nimmt der Editor nicht an. */
export const MAX_LISTEN_SPALTEN = 40;

/**
 * index.html:7463 - das Praefix ist tragend, siehe Eigenheit 8. Seit dem 2026-08-15 in
 * `lib/orderListEditor.ts` beheimatet (der Zelleneditor braucht es beim SCHREIBEN, dieser Dialog
 * beim ANLEGEN - und `lib/` darf nicht auf `components/` zeigen); hier nur noch durchgereicht,
 * damit die Aufrufstellen unveraendert bleiben.
 */
export { EIGENE_SPALTE_PRAEFIX };

/** `uid()` (index.html:4675) */
const uid = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/**
 * `saveOrderListEditor` (index.html:7483-7490) ohne Aussenwirkungen: entweder die neue Spaltenliste
 * oder die Meldung, die der Bestand als `toast(...)` ausgibt.
 */
export function naechsteListenSpalten(
  entwurf: readonly OrderListColumn[],
): { spalten: OrderListColumn[]; meldung?: undefined } | { spalten?: undefined; meldung: string } {
  // index.html:7485 - stiller Abbruch bei leerem Entwurf.
  if (!entwurf.length) return { meldung: "" };
  const labels = entwurf.map((column) => String(column.label || "").trim());
  if (labels.some((label) => !label)) return { meldung: "Bitte jede Spalte benennen" };
  if (new Set(labels.map((label) => label.toLowerCase())).size !== labels.length) {
    return { meldung: "Spaltennamen müssen eindeutig sein" };
  }
  // Eigenheit 7: die vierte Sperre, die es im Board-Editor nicht gibt.
  if (!entwurf.some((column) => column.visible !== false)) {
    return { meldung: "Mindestens eine Spalte muss sichtbar bleiben" };
  }
  return {
    spalten: entwurf.map((column, index) => ({
      ...column,
      label: labels[index],
      position: index,
      // Eigenheit 10: hier wird `visible` zum echten Wahrheitswert.
      visible: column.visible !== false,
    })),
  };
}

/** `saveOrderListEditor` (index.html:7491-7494) - die Werte geloeschter eigener Spalten entfernen. */
export function bereinigteBestellungen(
  bestellungen: readonly Order[],
  entfernteSchluessel: readonly string[],
): Order[] {
  return bestellungen.map((order) => {
    if (!order.customFields) return order;
    const felder = { ...(order.customFields as Record<string, unknown>) };
    let getroffen = false;
    for (const key of entfernteSchluessel) {
      if (key in felder) {
        delete felder[key];
        getroffen = true;
      }
    }
    return getroffen ? ({ ...order, customFields: felder } as Order) : order;
  });
}

/** `saveOrderListEditor` (index.html:7495-7496) - Filter unsichtbarer Spalten verwerfen. */
export function bereinigteFilter(
  filter: Readonly<Record<string, string>>,
  spalten: readonly OrderListColumn[],
): Record<string, string> {
  const visibleKeys = new Set(
    spalten.filter((column) => column.visible !== false).map((column) => column.key),
  );
  return Object.fromEntries(Object.entries(filter).filter(([key]) => visibleKeys.has(key)));
}

/**
 * `saveOrderListEditor` (index.html:7497) - zeigt die Sortierung auf eine unsichtbare Spalte,
 * springt sie auf die ERSTE sichtbare, immer mit `asc`.
 */
export function bereinigteSortierung(
  sortierung: { key: string; direction: string },
  spalten: readonly OrderListColumn[],
): { key: string; direction: string } {
  const sichtbare = spalten.filter((column) => column.visible !== false);
  if (sichtbare.some((column) => column.key === sortierung.key)) return sortierung;
  return { key: sichtbare[0].key, direction: "asc" };
}

export interface ListenEditorProps {
  /** `DB.orderListColumns` - leer oder fehlend faellt auf `DEFAULT_ORDER_LIST_COLUMNS` zurueck. */
  spalten?: readonly OrderListColumn[] | null;
  /** `DB.orders` - fuer die Zaehlung der belegten Werte einer eigenen Spalte (Eigenheit 9). */
  bestellungen?: readonly Order[];
  /** Siehe `SpaltenEditor`: der Bestand prueft `manage_settings` beim Oeffnen und beim Uebernehmen. */
  rechte?: ReadonlySet<string>;
  /** `DB.orderListColumns = …` (7490) plus die Schluessel, deren Werte zu loeschen sind (7493). */
  onSpeichern: (spalten: OrderListColumn[], entfernteSchluessel: string[]) => void;
  /** `closeOrderListEditor()` (7420). */
  onSchliessen: () => void;
  /** `confirm(...)` (7477) - als Prop, damit der Test antworten kann. */
  onRueckfrage?: (text: string) => boolean;
}

export function ListenEditor({
  spalten,
  bestellungen = [],
  rechte,
  onSpeichern,
  onSchliessen,
  onRueckfrage,
}: ListenEditorProps) {
  const [entwurf, setzeEntwurf] = useState<OrderListColumn[]>(() =>
    (spalten?.length ? spalten : DEFAULT_ORDER_LIST_COLUMNS)
      .map((column) => ({ ...column }))
      .sort((a, b) => Number(a.position || 0) - Number(b.position || 0)),
  );
  /** `orderListRemovedCustom` (index.html:7414) */
  const entfernt = useRef<string[]>([]);
  const [neueId, setzeNeueId] = useState<string | null>(null);

  // Stapel statt 2,2-s-Toast: jede Meldung hier sagt, dass die Absicht des Nutzers nicht ausgefuehrt wurde.
  const meldung = (text: string): void => meldeFehler(text);
  const rueckfrage = (text: string): boolean =>
    onRueckfrage ? onRueckfrage(text) : window.confirm(text);

  /** `setOrderListColumnField` (index.html:7444) - Weissliste `label`/`visible`. */
  const setzeFeld = (id: string, key: "label" | "visible", wert: string | boolean): void =>
    setzeEntwurf((vorher) =>
      vorher.map((column) => (column.id === id ? { ...column, [key]: wert } : column)),
    );

  /** `moveOrderListColumn` (index.html:7450) */
  const verschiebe = (id: string, richtung: number): void =>
    setzeEntwurf((vorher) => {
      const from = vorher.findIndex((column) => column.id === id);
      const to = from + Number(richtung);
      if (from < 0 || to < 0 || to >= vorher.length) return vorher;
      const naechste = vorher.slice();
      const [column] = naechste.splice(from, 1);
      naechste.splice(to, 0, column);
      return naechste;
    });

  /** `addOrderListColumn` (index.html:7460) */
  const ergaenze = (): void => {
    if (entwurf.length >= MAX_LISTEN_SPALTEN) return meldung("Maximal 40 Listenspalten möglich");
    const id = EIGENE_SPALTE_PRAEFIX + uid();
    // Eigenheit 8: `id` und `key` sind derselbe Wert.
    setzeEntwurf((vorher) => [
      ...vorher,
      { id, key: id, label: "Neue Spalte", position: vorher.length, visible: true, custom: true },
    ]);
    setzeNeueId(id);
  };

  /** `deleteOrderListColumn` (index.html:7472) */
  const loesche = (id: string): void => {
    const column = entwurf.find((item) => item.id === id);
    // Eigenheit 6: die zweite, unabhaengige Pruefung des Bestands.
    if (!column?.custom) return;
    const count = bestellungen.filter((order) =>
      String((order.customFields as Record<string, unknown>)?.[column.key] || "").trim(),
    ).length;
    if (
      count &&
      !rueckfrage(
        `In „${column.label}“ sind bei ${count} ${count === 1 ? "Bestellung" : "Bestellungen"} Werte gespeichert.\n\nSpalte und Werte wirklich löschen?`,
      )
    ) {
      return;
    }
    entfernt.current.push(column.key);
    setzeEntwurf((vorher) => vorher.filter((item) => item.id !== id));
  };

  /** `saveOrderListEditor` (index.html:7483) */
  const speichere = (): void => {
    if (rechte && !rechte.has("manage_settings")) {
      return meldung("Du darfst die Spalten nicht konfigurieren");
    }
    const ergebnis = naechsteListenSpalten(entwurf);
    if (ergebnis.meldung !== undefined) {
      if (ergebnis.meldung) meldung(ergebnis.meldung);
      return;
    }
    onSpeichern(ergebnis.spalten, [...entfernt.current]);
  };

  return (
    <div
      id="orderListEditorOverlay"
      className="board-editor-overlay open"
      role="presentation"
      onClick={(ereignis) => {
        if (ereignis.target === ereignis.currentTarget) onSchliessen();
      }}
    >
      <section
        className="board-editor-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="orderListEditorTitle"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="orderListEditorTitle">Bestellliste anpassen</h2>
            <p>Spalten einblenden, benennen, sortieren oder eigene Textfelder ergänzen.</p>
          </div>
          <button className="icon-btn" type="button" onClick={onSchliessen} title="Bearbeitungsmodus schließen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <div className="board-editor-list" id="orderListEditorList">
            {entwurf.map((column, index) => (
              <div className="list-column-row" key={column.id} data-column-id={column.id}>
                <div className="board-column-index">{String(index + 1).padStart(2, "0")}</div>
                <input
                  className="list-column-visible"
                  type="checkbox"
                  // Eigenheit 3: ein fehlendes Feld gilt als sichtbar.
                  checked={column.visible !== false}
                  aria-label={`${column.label} anzeigen`}
                  onChange={(ereignis) => setzeFeld(column.id, "visible", ereignis.target.checked)}
                />
                <input
                  className="board-column-name"
                  value={column.label}
                  // Eigenheit 4: 50 hier, 40 im Board-Editor.
                  maxLength={50}
                  aria-label="Spaltenname"
                  autoFocus={column.id === neueId}
                  onChange={(ereignis) => setzeFeld(column.id, "label", ereignis.target.value)}
                />
                <div className="list-column-kind">
                  {column.custom ? "Eigenes Textfeld" : "Standardfeld"}
                </div>
                <div className="board-column-actions">
                  <button
                    type="button"
                    onClick={() => verschiebe(column.id, -1)}
                    disabled={index === 0}
                    title="Nach links verschieben"
                  >
                    <Ikone name="arrow-left" />
                  </button>
                  <button
                    type="button"
                    onClick={() => verschiebe(column.id, 1)}
                    disabled={index === entwurf.length - 1}
                    title="Nach rechts verschieben"
                  >
                    <Ikone name="arrow-right" />
                  </button>
                  <button
                    className="delete"
                    type="button"
                    onClick={() => loesche(column.id)}
                    // Eigenheit 6: gesperrt bei Standardspalten.
                    disabled={!column.custom}
                    title="Eigene Spalte löschen"
                  >
                    <Ikone name="trash-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button className="board-editor-add icon-label" type="button" onClick={ergaenze}>
            <Ikone name="circle-plus" />
            Eigene Spalte hinzufügen
          </button>
          <div className="board-editor-note">
            Ausgeblendete Standardspalten können jederzeit wieder aktiviert werden. Die meisten
            sichtbaren Werte lassen sich direkt in der Bestellliste bearbeiten. Die Breiten werden
            beim Ziehen automatisch für diese Ansicht gespeichert.
          </div>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onSchliessen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={speichere}>
            <Ikone name="save" />
            Änderungen übernehmen
          </button>
        </div>
      </section>
    </div>
  );
}
